import { ID, includesInLC } from "@datassential/platform-ui-lib";
import dayjs, { Dayjs } from "dayjs";
import { components } from "@apiSchema";
import { v4 as uuid } from "uuid";

export interface ISocialPost {
    id: ID;
    source: string;
    sentiment: string;
    text: string;
    date: string;
    engagement: number;
    url: string;
}

export interface ISocialPosts {
    dateRangeStr: string;
    regular: ISocialPost[];
    newest: ISocialPost[];
    negative: ISocialPost[];
    positive: ISocialPost[];
}

export type ApiModel = components["schemas"]["SocialKeywordPostsCollectionViewModel"];

interface IData {
    apiModel: ApiModel;
}

const getFakePost = (): ISocialPost => {
    return {
        id: uuid(),
        source: SocialPosts.getPostSource('no data'),
        sentiment: 'neutral',
        text: 'Lorem ipsum dolor sit amet, consectetur adipisicing elit. Amet corporis culpa deleniti doloremque ea error eveniet excepturi harum in inventore iure laborum nihil nisi nobis, nulla odio omnis optio quos ratione rem sed suscipit tempora temporibus ullam velit vitae voluptatem! Aliquid aspernatur culpa illum impedit magni odit quam quidem, voluptates?',
        date: dayjs().format(),
        engagement: Math.floor(Math.random() * 100),
        url: '',
    };
};

export class SocialPosts implements ISocialPosts {
    static readonly defaultData: ISocialPosts = {
        dateRangeStr: '',
        regular: [],
        newest: [],
        negative: [],
        positive: [],
    }

    static readonly fakeData: ISocialPosts = {
        dateRangeStr: SocialPosts.getDateRange(dayjs().add(-3, 'month'), dayjs()),
        regular: [
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('Twitter'),
                sentiment: 'positive',
                url: 'https://twitter.com',
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('Instagram'),
                sentiment: 'negative',
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('Reddit'),
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('TikTok'),
                sentiment: 'positive',
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('Pinterest'),
                sentiment: 'negative',
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('YouTube'),
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('Tumblr'),
                sentiment: 'positive',
            },
            {
                ...getFakePost(),
                source: SocialPosts.getPostSource('Facebook'),
                sentiment: 'negative',
            },
            {
                ...getFakePost(),
            },
        ],
        newest: [],
        negative: [],
        positive: [],
    }

    dateRangeStr = SocialPosts.defaultData.dateRangeStr;
    regular = SocialPosts.defaultData.regular;
    newest = SocialPosts.defaultData.newest;
    negative = SocialPosts.defaultData.negative;
    positive = SocialPosts.defaultData.positive;

    constructor(data?: IData) {
        if (data?.apiModel) {
            this.mapFromApiModel(data.apiModel);
        }
    }

    private setData(data: Partial<ISocialPosts>) {
        Object.assign(this, data);
    }

    private mapFromApiModel(apiModel: ApiModel) {
        this.setData({
            dateRangeStr: SocialPosts.getDateRange(
                dayjs(apiModel.initial?.startDate),
                dayjs(apiModel.initial?.endDate)
            ),
            regular: this.mapPosts(apiModel.initial?.posts ?? []),
            newest: this.mapPosts(apiModel.newest?.posts ?? []),
            negative: this.mapPosts(apiModel.negative?.posts ?? []),
            positive: this.mapPosts(apiModel.positive?.posts ?? []),
        });
    }

    static getDateRange(startDate: Dayjs, endDate: Dayjs): string {
        const startYear = startDate.year();
        const endYear = endDate.year();

        if (startYear === endYear) {
            if (startDate.month() === endDate.month()) {
                return startDate.format('MMM YYYY');
            }
            return `${startDate.format('MMM')} - ${endDate.format('MMM YYYY')}`;
        }

        return `${startDate.format('MMM YYYY')} - ${endDate.format('MMM YYYY')}`;
    }

    private mapPosts(data: components["schemas"]["SocialKeywordPostViewModel"][]): ISocialPost[] {
        return data.map(i => ({
            id: uuid(),
            source: SocialPosts.getPostSource(i.type || ''),
            text: i.content || '',
            sentiment: i.sentiment || 'neutral',
            date: i.date || '',
            engagement: i.engagementCount || 0,
            url: i.url ?? '',
        }));
    }

    static formatDate(dateRaw: string): string {
        return dayjs(dateRaw).format('MM/DD/YYYY');
    }

    static getPostSource(sourceRaw: string): string {
        if (includesInLC(sourceRaw, 'facebook')) {
            return 'Facebook';
        }
        if (includesInLC(sourceRaw, 'twitter')) {
            return 'Twitter';
        }
        if (includesInLC(sourceRaw, 'instagram')) {
            return 'Instagram';
        }
        if (includesInLC(sourceRaw, 'pinterest')) {
            return 'Pinterest';
        }
        if (includesInLC(sourceRaw, 'youtube')) {
            return 'Youtube';
        }
        if (includesInLC(sourceRaw, 'tiktok')) {
            return 'TikTok';
        }
        if (includesInLC(sourceRaw, 'tumblr')) {
            return 'Tumblr';
        }
        if (includesInLC(sourceRaw, 'reddit')) {
            return 'Reddit';
        }
        return 'Forums';
    }
}